// Runtime validation for every system boundary: HTTP request bodies and
// WebSocket frames. Nothing reaches the room registry or a store without passing
// through one of these, so a malformed agent skeleton fails fast with the exact
// path that was wrong instead of half-applying to a live board.

import { z } from "zod";
import { LAYOUT_DIRECTIONS, LAYOUT_MODES } from "./layout.js";

const COLOR = z.string().regex(/^(#[0-9a-fA-F]{3,8}|transparent)$/, "expected a hex colour or 'transparent'");
const ELEMENT_ID = z.string().min(1).max(200);

// ---- authoring: the skeleton format shared with excalidraw-cowork ----

export const nodeSchema = z.object({
  id: ELEMENT_ID,
  type: z.enum(["rectangle", "ellipse", "diamond"]).optional(),
  label: z.string().max(10000).optional(),
  x: z.number().finite().optional(),
  y: z.number().finite().optional(),
  w: z.number().finite().positive().optional(),
  h: z.number().finite().positive().optional(),
  bg: COLOR.optional(),
  stroke: COLOR.optional(),
  labelColor: COLOR.optional(),
  fillStyle: z.enum(["solid", "hachure", "cross-hatch"]).optional(),
  strokeWidth: z.number().positive().max(20).optional(),
  strokeStyle: z.enum(["solid", "dashed", "dotted"]).optional(),
  roughness: z.number().min(0).max(2).optional(),
  fontSize: z.number().positive().max(200).optional(),
  font: z.enum(["hand", "normal", "code"]).optional(),
  // Where the label sits in its box: left and top for code, JSON or a list.
  align: z.enum(["left", "center", "right"]).optional(),
  verticalAlign: z.enum(["top", "middle"]).optional(),
  group: ELEMENT_ID.optional(),
  // The swimlane a step sits in; only with `lanes`.
  lane: ELEMENT_ID.optional(),
});

// A boundary drawn around the nodes that name it in `group`: an account, a
// network, a team, a system. Groups nest through `parent`.
export const groupSchema = z.object({
  id: ELEMENT_ID,
  label: z.string().max(200).optional(),
  parent: ELEMENT_ID.optional(),
  x: z.number().finite().optional(),
  y: z.number().finite().optional(),
  w: z.number().finite().positive().optional(),
  h: z.number().finite().positive().optional(),
  bg: COLOR.optional(),
  stroke: COLOR.optional(),
  labelColor: COLOR.optional(),
  strokeWidth: z.number().positive().max(20).optional(),
  strokeStyle: z.enum(["solid", "dashed", "dotted"]).optional(),
  roughness: z.number().min(0).max(2).optional(),
  fontSize: z.number().positive().max(200).optional(),
  font: z.enum(["hand", "normal", "code"]).optional(),
});

// A swimlane: a role or team, drawn as a band across the whole diagram. The
// order given is the order top to bottom.
export const laneSchema = z.object({
  id: ELEMENT_ID,
  label: z.string().max(200).optional(),
  bg: COLOR.optional(),
  stroke: COLOR.optional(),
  labelColor: COLOR.optional(),
  fontSize: z.number().positive().max(200).optional(),
  font: z.enum(["hand", "normal", "code"]).optional(),
});

// One entry per colour or line style that means something.
export const legendSchema = z.object({
  label: z.string().min(1).max(80),
  type: z.enum(["rectangle", "ellipse", "diamond"]).optional(),
  bg: COLOR.optional(),
  stroke: COLOR.optional(),
  strokeStyle: z.enum(["solid", "dashed", "dotted"]).optional(),
  roughness: z.number().min(0).max(2).optional(),
  font: z.enum(["hand", "normal", "code"]).optional(),
});

// Excalidraw 0.18's arrowheads. The crow's feet are for ERDs: crowfoot_one is
// exactly one, crowfoot_many is many, circle_outline marks "zero" (optional).
const ARROWHEAD = z.enum([
  "arrow", "bar", "dot", "circle", "circle_outline", "triangle", "triangle_outline",
  "diamond", "diamond_outline", "crowfoot_one", "crowfoot_many", "crowfoot_one_or_many",
]);

export const edgeSchema = z.object({
  id: ELEMENT_ID.optional(),
  from: ELEMENT_ID,
  to: ELEMENT_ID,
  label: z.string().max(2000).optional(),
  style: z.enum(["elbow", "straight"]).optional(),
  stroke: COLOR.optional(),
  labelColor: COLOR.optional(),
  labelBg: COLOR.optional(),
  strokeWidth: z.number().positive().max(20).optional(),
  strokeStyle: z.enum(["solid", "dashed", "dotted"]).optional(),
  startArrowhead: ARROWHEAD.nullable().optional(),
  endArrowhead: ARROWHEAD.nullable().optional(),
  gap: z.number().min(0).max(100).optional(),
  fontSize: z.number().positive().max(200).optional(),
  font: z.enum(["hand", "normal", "code"]).optional(),
});

export const textSchema = z.object({
  id: ELEMENT_ID.optional(),
  text: z.string().min(1).max(10000),
  x: z.number().finite().optional(),
  y: z.number().finite().optional(),
  fontSize: z.number().positive().max(200).optional(),
  font: z.enum(["hand", "normal", "code"]).optional(),
  stroke: COLOR.optional(),
  align: z.enum(["left", "center", "right"]).optional(),
});

// ---- sequence diagrams: participants across the top, messages down the page ----

export const participantSchema = nodeSchema.pick({
  id: true, label: true, type: true, bg: true, stroke: true, labelColor: true, fillStyle: true,
  strokeWidth: true, roughness: true, fontSize: true, font: true,
});

export const messageSchema = z.object({
  id: ELEMENT_ID.optional(),
  from: ELEMENT_ID,
  to: ELEMENT_ID,
  label: z.string().max(2000).optional(),
  kind: z.enum(["sync", "async", "reply"]).optional(),
  stroke: COLOR.optional(),
  labelColor: COLOR.optional(),
  strokeWidth: z.number().positive().max(20).optional(),
  fontSize: z.number().positive().max(200).optional(),
  font: z.enum(["hand", "normal", "code"]).optional(),
});

export const noteSchema = z.object({
  id: ELEMENT_ID.optional(),
  note: z.string().min(1).max(2000),
  over: z.union([ELEMENT_ID, z.tuple([ELEMENT_ID, ELEMENT_ID])]),
  bg: COLOR.optional(),
  stroke: COLOR.optional(),
});

const FRAGMENTS = ["alt", "opt", "loop", "par", "critical", "break"];

// An item is a fragment, a note or a message, told apart by its key. The error of
// a bad one keeps its path, so it reads `messages.3.messages.0.to: Required`.
const item = z.any().transform((value, ctx) => {
  const schema = value?.fragment !== undefined ? fragmentSchema : value?.note !== undefined ? noteSchema : messageSchema;
  const result = schema.safeParse(value);
  if (result.success) return result.data;
  for (const issue of result.error.issues) ctx.addIssue({ code: "custom", message: issue.message, path: issue.path });
  return z.NEVER;
});
const items = z.array(item).min(1).max(500);

export const fragmentSchema = z
  .object({
    id: ELEMENT_ID.optional(),
    fragment: z.enum(FRAGMENTS),
    label: z.string().max(200).optional(),
    messages: z.lazy(() => items),
    else: z.array(z.object({ label: z.string().max(200).optional(), messages: z.lazy(() => items) })).min(1).optional(),
  })
  .refine((f) => f.else === undefined || f.fragment === "alt" || f.fragment === "par", {
    message: "only alt and par have an else branch",
    path: ["else"],
  });

const MAX_ELEMENTS = 5000;

const SECTION = z.string().trim().min(1).max(80);

export const skeletonSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  section: SECTION.optional(),
  layout: z.enum(LAYOUT_MODES).optional(),
  direction: z.enum(LAYOUT_DIRECTIONS).optional(),
  spacing: z.object({ node: z.number().positive().max(1000).optional(), layer: z.number().positive().max(1000).optional() }).optional(),
  appState: z.object({ viewBackgroundColor: COLOR.optional() }).partial().optional(),
  nodes: z.array(nodeSchema).max(MAX_ELEMENTS).optional(),
  edges: z.array(edgeSchema).max(MAX_ELEMENTS).optional(),
  texts: z.array(textSchema).max(MAX_ELEMENTS).optional(),
  groups: z.array(groupSchema).max(MAX_ELEMENTS).optional(),
  lanes: z.array(laneSchema).min(1).max(20).optional(),
  participants: z.array(participantSchema).min(1).max(30).optional(),
  messages: items.optional(),
  numbered: z.boolean().optional(),
  legend: z.array(legendSchema).max(20).optional(),
  defaults: z
    .object({
      node: nodeSchema.omit({ id: true, x: true, y: true, label: true, group: true, lane: true }).partial().optional(),
      edge: edgeSchema.omit({ id: true, from: true, to: true, label: true }).partial().optional(),
      group: groupSchema.omit({ id: true, x: true, y: true, label: true, parent: true }).partial().optional(),
    })
    .optional(),
});

export const patchSchema = z
  .object({
    add: z
      .object({
        nodes: z.array(nodeSchema).max(MAX_ELEMENTS).optional(),
        edges: z.array(edgeSchema).max(MAX_ELEMENTS).optional(),
        texts: z.array(textSchema).max(MAX_ELEMENTS).optional(),
      })
      .optional(),
    update: z
      .array(
        z.object({
          id: ELEMENT_ID,
          x: z.number().finite().optional(),
          y: z.number().finite().optional(),
          w: z.number().finite().positive().optional(),
          h: z.number().finite().positive().optional(),
          label: z.string().max(10000).optional(),
          text: z.string().max(10000).optional(),
          bg: COLOR.optional(),
          stroke: COLOR.optional(),
        }),
      )
      .max(MAX_ELEMENTS)
      .optional(),
    delete: z.array(ELEMENT_ID).max(MAX_ELEMENTS).optional(),
  })
  .refine((p) => p.add || p.update || p.delete, {
    message: "a patch needs at least one of add, update or delete",
  });

// ---- content management ----

const NAME = z.string().trim().min(1, "name cannot be empty").max(120);
const FOLDER_ID = z.string().regex(/^fld_[0-9a-f]{12}$/);

export const MAX_TAGS = 8;

// Tags are normalised on the way in rather than validated as typed, so "Q3 "
// and "q3" are the same tag and a filter over them is a plain equality check.
const TAG = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, "a tag cannot be empty")
  .max(24, "a tag can be at most 24 characters")
  .regex(/^[a-z0-9][a-z0-9 _-]*$/, "a tag may hold letters, digits, spaces, dashes and underscores");

// Duplicates are dropped rather than refused: the same tag twice is a slip, not
// something worth failing a board rename over.
const TAGS = z
  .array(TAG)
  .max(MAX_TAGS, `a board can carry at most ${MAX_TAGS} tags`)
  .transform((tags) => [...new Set(tags)]);

export const createFolderSchema = z.object({
  name: NAME,
  color: COLOR.optional(),
});

export const updateFolderSchema = z.object({
  name: NAME.optional(),
  color: COLOR.optional(),
});

export const createBoardSchema = z.object({
  name: NAME,
  folderId: FOLDER_ID,
  tags: TAGS.optional(),
});

export const updateBoardSchema = z
  .object({
    name: NAME.optional(),
    folderId: FOLDER_ID.optional(),
    tags: TAGS.optional(),
  })
  .refine((p) => p.name !== undefined || p.folderId !== undefined || p.tags !== undefined, {
    message: "nothing to change",
  });

export const allowlistSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(200),
  note: z.string().max(500).optional(),
});

export const ROLES = ["member", "admin"];

export const updateRoleSchema = z.object({
  role: z.enum(ROLES),
});

// An invitation only ever grants admin. Somebody who has never signed in has no
// role to take away, and letting an invite carry "member" would give it a way to
// demote — including a way to demote the last admin, which the role endpoint
// deliberately refuses.
export const inviteSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(200),
  note: z.string().max(500).optional(),
});

// ---- comments ----

const COMMENT_BODY = z
  .string()
  .trim()
  .min(1, "a comment cannot be empty")
  .max(4000, "a comment can be at most 4000 characters");
const COMMENT_ID = z.string().regex(/^cmt_[0-9a-z]{9}[0-9a-f]{6}$/, "not a comment id");

// Where a thread sits. Name an element, or give a point on the canvas, or both.
// What is left out is filled in by the server (see resolveAnchor in comments.js).
export const commentAnchorSchema = z
  .object({
    elementId: ELEMENT_ID.optional(),
    u: z.number().min(0).max(1).optional(),
    v: z.number().min(0).max(1).optional(),
    x: z.number().finite().optional(),
    y: z.number().finite().optional(),
  })
  .refine((a) => (a.u === undefined) === (a.v === undefined), { message: "u and v go together" })
  .refine((a) => (a.x === undefined) === (a.y === undefined), { message: "x and y go together" })
  .refine((a) => a.elementId !== undefined || a.x !== undefined, {
    message: "an anchor needs an elementId, or x and y",
  });

// A new thread carries an anchor, a reply carries the id of the thread it joins.
export const createCommentSchema = z
  .object({
    body: COMMENT_BODY,
    anchor: commentAnchorSchema.optional(),
    parentId: COMMENT_ID.optional(),
  })
  .refine((c) => c.anchor !== undefined || c.parentId !== undefined, {
    message: "a comment needs an anchor (a new thread) or a parentId (a reply)",
  })
  .refine((c) => c.anchor === undefined || c.parentId === undefined, {
    message: "a reply cannot have an anchor",
  });

export const resolveCommentSchema = z.object({ resolved: z.boolean() });

// ---- realtime frames ----

// Excalidraw elements are passed through rather than fully described: the schema
// checks the fields reconciliation depends on and lets the rest ride, because
// pinning every element field here would break on the next Excalidraw release
// for no safety gain.
export const elementSchema = z
  .object({
    id: ELEMENT_ID,
    type: z.string().min(1).max(50),
    version: z.number().int().nonnegative().optional(),
    versionNonce: z.number().int().optional(),
    isDeleted: z.boolean().optional(),
    updated: z.number().int().nonnegative().optional(),
  })
  .passthrough();

export const clientFrameSchema = z.discriminatedUnion("t", [
  z.object({
    t: z.literal("update"),
    elements: z.array(elementSchema).max(MAX_ELEMENTS),
  }),
  z.object({
    t: z.literal("pointer"),
    x: z.number().finite(),
    y: z.number().finite(),
    button: z.enum(["up", "down"]).optional(),
    selectedIds: z.array(ELEMENT_ID).max(500).optional(),
  }),
  z.object({ t: z.literal("ping") }),
]);

/**
 * Parse a value against a schema and return a plain result rather than throwing,
 * so route handlers and the socket loop share one error shape.
 * @returns {{ok: true, value: T} | {ok: false, error: string, issues: object[]}}
 */
export const parse = (schema, value) => {
  const result = schema.safeParse(value);
  if (result.success) return { ok: true, value: result.data };
  const issues = result.error.issues.map((i) => ({
    path: i.path.join("."),
    message: i.message,
  }));
  const first = issues[0];
  return {
    ok: false,
    error: first ? `${first.path || "body"}: ${first.message}` : "invalid input",
    issues,
  };
};
